import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';

import { API_URL_V4 } from 'src/environments/environment.local';
import * as formatDataHelpers from '../helpers/format-browse-data.helpers';
import { Sport } from '../models/sport';
import { BrowseTypesEnum } from '../models/browse-types.enum';
import {
  GpsOnly,
  VideoSection,
  Coaching,
  Workout,
} from '@kinomap-dev/ng-ui-lib';
import { Playlist } from '@kinomap-dev/ng-ui-lib/lib/models/playlist.model';

@Injectable({ providedIn: 'root' })
export class FavoriteApi {
  private readonly favoriteUrl: string = `${API_URL_V4}/me/favorites`;
  private lastSelectedSport: Sport | null = null;

  private readonly videoLimit: number = 15;
  private videoParams: HttpParams = new HttpParams();
  isVideoLastPageLoaded: boolean = false;

  private readonly gpsLimit: number = 15;
  private gpsParams: HttpParams = new HttpParams();
  isGpsLastPageLoaded: boolean = false;

  private readonly workoutLimit: number = 15;
  private workoutParams: HttpParams = new HttpParams();
  isWorkoutLastPageLoaded: boolean = false;

  private readonly coachingLimit: number = 15;
  private coachingParams: HttpParams = new HttpParams();
  isCoachingLastPageLoaded: boolean = false;

  private readonly playlistLimit: number = 15;
  private playlistParams: HttpParams = new HttpParams();
  isPlaylistLastPageLoaded: boolean = false;

  constructor(private http: HttpClient) {
    this.videoParams = this.videoParams
      .set('limit', this.videoLimit)
      .set('withoutCoaching', true)
      .set('page', 1);
    this.gpsParams = this.gpsParams.set('limit', this.gpsLimit).set('page', 1);
    this.workoutParams = this.workoutParams
      .set('limit', this.workoutLimit)
      .set('page', 1);
    this.coachingParams = this.coachingParams
      .set('limit', this.coachingLimit)
      .set('page', 1);
    this.playlistParams = this.playlistParams
      .set('limit', this.playlistLimit)
      .set('page', 1);
  }

  getFavorites$(
    selectedSport: Sport = this.lastSelectedSport,
    nextPage: boolean,
    contentType: BrowseTypesEnum | string
  ): Observable<
    VideoSection[] | GpsOnly[] | Workout[] | Coaching[] | Playlist[]
  > {
    const {
      getParams,
      setParams,
      setIsLastPageLoaded,
      endpoint,
      convertApiData,
    } = this.defineVariablesWithContentType(contentType);

    setParams(getParams().set('sport', selectedSport.name));

    if (!nextPage) {
      setParams(getParams().set('page', 1));
      setIsLastPageLoaded(false);
    }

    return this.http
      .get(`${this.favoriteUrl}/${endpoint}`, { params: getParams() })
      .pipe(
        map((res: any) => {
          const currentPage: number = +res.meta.current_page;
          const lastPage: number = +res.meta.last_page;

          if (currentPage === lastPage) {
            setIsLastPageLoaded(true);
          } else {
            setParams(getParams().set('page', currentPage + 1));
          }

          this.lastSelectedSport = { ...selectedSport };

          return res.data.map((item) => {
            return { ...convertApiData(item), isFavorite: true };
          });
        })
      );
  }

  postFavorite$(
    id: number,
    contentType: BrowseTypesEnum | string
  ): Observable<any> {
    const { endpoint } = this.defineVariablesWithContentType(contentType);
    return this.http.post(`${this.favoriteUrl}/${endpoint}/${id}`, {});
  }

  deleteFavorite$(
    id: number,
    contentType: BrowseTypesEnum | string
  ): Observable<any> {
    const { endpoint } = this.defineVariablesWithContentType(contentType);
    return this.http.delete(`${this.favoriteUrl}/${endpoint}/${id}`);
  }

  private defineVariablesWithContentType(
    contentType: BrowseTypesEnum | string
  ): {
    getParams: () => HttpParams;
    setParams: (params: HttpParams) => void;
    setIsLastPageLoaded: (value: boolean) => void;
    endpoint: string;
    convertApiData: (
      apiData: any
    ) => VideoSection | GpsOnly | Workout | Coaching | Playlist;
  } {
    const obj: {
      [key in BrowseTypesEnum]:
        | {
            getParams: () => HttpParams;
            setParams: (params: HttpParams) => void;
            setIsLastPageLoaded: (value: boolean) => void;
            endpoint: string;
            convertApiData: (
              apiData: any
            ) => VideoSection | GpsOnly | Workout | Coaching | Playlist;
          }
        | undefined;
    } = {
      video: {
        getParams: () => this.videoParams,
        setParams: (params) => (this.videoParams = params),
        setIsLastPageLoaded: (value) => (this.isVideoLastPageLoaded = value),
        endpoint: 'scenics',
        convertApiData: formatDataHelpers.convertApiDataToVideo,
      },
      'gps-track': {
        getParams: () => this.gpsParams,
        setParams: (params) => (this.gpsParams = params),
        setIsLastPageLoaded: (value) => (this.isGpsLastPageLoaded = value),
        endpoint: 'mapmodes',
        convertApiData: formatDataHelpers.convertApiDataToGpsTrack,
      },
      workout: {
        getParams: () => this.workoutParams,
        setParams: (params) => (this.workoutParams = params),
        setIsLastPageLoaded: (value) => (this.isWorkoutLastPageLoaded = value),
        endpoint: 'workouts',
        convertApiData: formatDataHelpers.convertApiDataToWorkout,
      },
      coaching: {
        getParams: () => this.coachingParams,
        setParams: (params) => (this.coachingParams = params),
        setIsLastPageLoaded: (value) => (this.isCoachingLastPageLoaded = value),
        endpoint: 'coachings',
        convertApiData: formatDataHelpers.convertApiMetaDataToCoaching,
      },
      playlist: {
        getParams: () => this.playlistParams,
        setParams: (params) => (this.playlistParams = params),
        setIsLastPageLoaded: (value) => (this.isPlaylistLastPageLoaded = value),
        endpoint: 'playlists',
        convertApiData: formatDataHelpers.convertApiDataToPlaylist,
      },
    };

    if (contentType === 'scenic') contentType = BrowseTypesEnum.Video;
    return obj[contentType];
  }
}
